import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';

import { startWith } from 'rxjs/operators';

import { TellentAppsDataFacade } from '@core/tellent-logic';
import {
  BRAND,
  SeatKind,
  SeatType,
  TellentBrand,
  TellentBrandsDefaultRoles,
  TellentRole,
} from '@core/tellent-types';

@Injectable({ providedIn: 'root' })
export class AccessRolesTableService {
  private readonly apps = inject(TellentAppsDataFacade);
  private readonly destroyRef = inject(DestroyRef);
  public readonly brand = inject(BRAND);

  public bindSeatLogic(seatKind: SeatKind, seatFormGroup: FormGroup<SeatType>) {
    const toggleCtrl = seatFormGroup.controls.toggle;
    const roleCtrl = seatFormGroup.controls.roleExternalId;

    toggleCtrl.valueChanges
      .pipe(startWith(toggleCtrl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(isOn => {
        const defaultRole: TellentRole | null = this.apps.defaultRoleBySeatKind(seatKind)() ?? null;

        if (!isOn) {
          // OFF → clear & disable
          roleCtrl.setValue(null, { emitEvent: false });
          roleCtrl.disable({ emitEvent: false });
        } else if (isOn) {
          // ON  → enable & maybe seed default
          roleCtrl.enable({ emitEvent: false });
          if (!roleCtrl.value && defaultRole?.externalId) {
            roleCtrl.setValue(defaultRole.externalId, { emitEvent: false });
          }
        }
      });
  }

  public applyDefaults(
    defaults: TellentBrandsDefaultRoles,
    recruiteeControl: FormGroup<SeatType>,
    kiwiControl: FormGroup<SeatType>,
  ): void {
    const isRecruiteeDefault = !!defaults.recruitee?.externalId;
    const isKiwiDefault = !!defaults.kiwi?.externalId;

    if (this.brand === TellentBrand.Tellent) {
      if (isKiwiDefault) {
        this.toggleKiwiRole(defaults, kiwiControl);
      }
      if (isRecruiteeDefault && !isKiwiDefault) {
        this.toggleRecruiteeRole(defaults, recruiteeControl);
      }
      return;
    }

    if (this.brand === TellentBrand.Recruitee && isRecruiteeDefault) {
      this.toggleRecruiteeRole(defaults, recruiteeControl);
    }

    if (this.brand === TellentBrand.Kiwi && isKiwiDefault) {
      this.toggleKiwiRole(defaults, kiwiControl);
    }
  }

  private toggleRecruiteeRole(
    defaults: TellentBrandsDefaultRoles,
    control: FormGroup<SeatType>,
  ): void {
    control.patchValue(
      {
        roleExternalId: defaults.recruitee!.externalId,
        toggle: true,
      },
      { emitEvent: false },
    );
  }

  private toggleKiwiRole(defaults: TellentBrandsDefaultRoles, control: FormGroup<SeatType>): void {
    control.patchValue(
      {
        roleExternalId: defaults.kiwi!.externalId,
        toggle: true,
      },
      { emitEvent: false },
    );
  }
}
